import React from "react";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import Utils from "../../utils.js";
import FeatherIcon from "feather-icons-react";
import { AuthContext } from "../../components/auth/AuthContext.js";
import { useSnackbar } from "../../components/toast/index.jsx";
import SettingHeader from "./setting-header.jsx";
import CreateUserComponent from "./create-user.jsx";
import { Grid } from "@mui/material";
import StatusChangeSwitch from "./status-change-switch.jsx";
import ChangeAdminSwitch from "./change-admin-switch.jsx";
import { COMPANY_UPDATED_FAILED, COMPANY_UPDATED_SUCCESS } from "../../helpers/messages.js";
import { getUserListAPI } from "../../services/user.js";
import { getUserGroupsAPI, addUserToGroupAPI, removeUserToGroupAPI } from "../../services/group.js";
import TableComponent from "../../components/table/list.jsx";
import { ADMIN_GROUP_NAME, LIST_NULL_OPTION_VALUE } from "../../helpers/constant.js";
import CommonSelectComponent from "../../components/table/filters/common-select.jsx";

function AdminUser() {
    let navigate = useNavigate();
    const { setUserGroups, user, refreshUser, isAdmin, isAuthenticated } = React.useContext(AuthContext); // Extract setUserGroups from AuthContext
    const [data, setData] = React.useState([]);
    const [companyList, setCompanyList] = React.useState([]);
    const [newCampDiag, setNewCampDiag] = React.useState(false);
    const [selectedTab] = React.useState("User setup");
    const [spinner, setSpinner] = React.useState(false);
    const showSnackbar = useSnackbar();
    const [allUsers, setAllUsers] = React.useState([]);
    const [updatingUserId, setUpdatingUserId] = React.useState(null);

    async function getGroupsForUser(userItem) {
        const userId = userItem.Attributes.find((z) => z.Name === "sub") ? userItem.Attributes.find((z) => z.Name === "sub")?.Value : "";
        const email = userItem.Attributes.find((z) => z.Name === "email") ? userItem.Attributes.find((z) => z.Name === "email")?.Value : "";
        const Enabled = userItem.Enabled || false;
        
        // to get user groups
        let groups = await getUserGroupsAPI({ Username: userId });
        let admin = false;
        if (groups.includes(ADMIN_GROUP_NAME)) {
            admin = true;
            groups = groups.filter((z) => z !== ADMIN_GROUP_NAME);
        }
        if (groups.length === 1) groups = groups[0];

        // to get first and last name
        let firstname = "", lastname = "";
        const response = await Utils.genericGet("getUser", userId);
        if (response) {
            firstname = response.data?.getUser?.firstname?.toLowerCase() || "";
            lastname = response.data?.getUser?.lastname?.toLowerCase() || "";
        }
        const fullName = (firstname + " " + lastname).trim() || "";

        // returning the user data
        return { userId, email, admin, group: groups, firstname, lastname, Enabled, fullName };
    }

    async function countUsers() {  
        let users = [];
        let data = await getUserListAPI({
            AttributesToGet: ["sub", "email"],
            Limit: 60,
        });

        users = users.concat(data.Users);
        while (data.PaginationToken !== undefined) {
            data = await getUserListAPI({
                AttributesToGet: ["sub", "email"],
                PaginationToken: data.PaginationToken,
                Limit: 60,
            });
            users = users.concat(data.Users);
        }
        const mainUserResponse = [...users];
        setAllUsers(mainUserResponse);

        return await Promise.all(mainUserResponse.map(getGroupsForUser));
    }

    async function getCompanyData() {
        Utils.sendProgressBar(true);
        setSpinner(true);
        
        let companies = await Utils.genericList("listCompanies", { limit: 1000 });
        companies = companies.data.listCompanies.items;
        setCompanyList(companies);
        let users = await countUsers();
        setData([...users]);
        setSpinner(false);
    }

    React.useEffect(() => {
        if (isAuthenticated) {
            if (!isAdmin) {
                navigate("/");
                return;
            }
            const fetchData = async () => {
                await refreshUser();
                await getCompanyData(); // Your async data fetching function
            };
            fetchData();
        }

        window.scrollTo(0, 0); // Scroll to top when component mounts
    }, [isAdmin, isAuthenticated]);

    // Method to update the user status.
    const userUpdateCallback = (user, key) => {
        const _data = [...data];
        _data.forEach((u) => {
            if (u.userId === user.userId) {
                u[key] = !user[key];
            }
        });
        setData([..._data])
    }

    // on change user company
    const onChangeCompanyHandler = async (value, item) => {
        try {
            setUpdatingUserId(item.userId);
            const adminSelected = value === ADMIN_GROUP_NAME;
            if(item.admin && adminSelected) {
                return;
            }
            Utils.sendProgressBar(true);
            if (item.group.length !== 0) {
                await removeUserToGroupAPI({
                    GroupName: Array.isArray(item.group) ? item.group[0] : item.group,
                    Username: item.userId,
                });
            }
            await addUserToGroupAPI({
                GroupName: value,
                Username: item.userId
            });

            // update the group in the data state
            const updateIndex = data.findIndex((u) => u.userId === item.userId);
            if(updateIndex !== -1) {
                data[updateIndex].group = value;
            }
            
            setData([...data]);
            showSnackbar(COMPANY_UPDATED_SUCCESS);
            setUpdatingUserId(null);
            for (const _u of allUsers) {
                if(_u.Username === item.userId) {
                    const updatedValue = await getGroupsForUser(_u);
                    if(updateIndex !== -1 && updatedValue) {
                        data[updateIndex] = updatedValue;
                    }
                    break;
                }
            }

            const updatedGroups = await Utils.refreshUserGroups();
            setUserGroups(updatedGroups); // Update AuthContext here
        } catch (error) {
            setUpdatingUserId(null);
            showSnackbar(COMPANY_UPDATED_FAILED, "error");
        }
    }

    // define table headers
    const columns = [
        {
            label: "User",
            key: "fullName",
            sortable: true,
            style: { width: "15%" },
            renderItem: (row) => row.fullName || "-",
        },
        {
            label: "Email",
            key: "email",
            sortable: true,
            renderItem: (row) => row.email || "-",
        },
        {
            label: "Admin",
            key: "admin",
            sortable: true,
            renderItem: (row) => (<ChangeAdminSwitch checked={row.admin} user={row} onSuccess={() => userUpdateCallback(row, 'admin')} />)
        },
        {
            label: "Status",
            key: "Enabled",
            sortable: true,
            renderItem: (row) => (<StatusChangeSwitch checked={row.Enabled} user={row} disabled={row.userId === user.username} onSuccess={() => userUpdateCallback(row, 'Enabled')} />)
        },
        // {
        //     label: "Role",
        //     key: "role",
        //     sortable: true,
        //     renderItem: (row) => row.role || "-",
        // },
        {
            label: "Company",
            key: "group",
            sortable: true,
            renderItem: (row, rowIndex, columnIndex) => (<CommonSelectComponent
                htmlId={`select-${rowIndex}-${columnIndex}`}
                label="Company"
                labelKey={"name"}
                valueKey={"internalname"}
                Placeholder={"Select Company"}
                size="small"
                spinner={spinner}
                filterOptions={companyList}
                selected={row.group}
                isLoading={updatingUserId === row.userId}
                setSelected={(value) => onChangeCompanyHandler(value, row)}
            />)
        }
    ];

    const headerActionButtons = () => {
        return (
            <Button
                variant="contained"
                onClick={() => setNewCampDiag(true)}
                startIcon={<FeatherIcon icon="plus-circle" size={20} />}
            >
                Create User
            </Button>
        )
    }

    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            <SettingHeader title={"Users"} selectedTab={selectedTab} />
            <Grid
                className="vocalysed-home-upadted setting-user-setup"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <TableComponent
                    headerActionButtons={headerActionButtons}
                    spinner={spinner}
                    data={data}
                    columns={columns}
                    pagination={true}
                    searchKeys={["fullName", "email", "group"]}
                    filterTypes={{ groupName: "group" }}
                    defaultFilterValues={{ groupName: LIST_NULL_OPTION_VALUE }}
                    noDataMessage={"No users found."}
                    companyListOptions={companyList}
                />
            </Grid>

            {/* Create User Modal */}
            <CreateUserComponent
                open={newCampDiag}
                companyList={companyList}
                onClose={async (inp) => {
                    if (inp === null) setNewCampDiag(false);
                    else {
                        getCompanyData();
                        setNewCampDiag(false);
                    }
                }}
            />
        </Grid>
    );
}

export default AdminUser;
